import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Quote } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const testimonials = [
  {
    quote:
      "Our fractional CTO shipped the architecture review in 10 days and saved us a bad vendor decision. The impact was immediate and measurable.",
    name: 'Priya S.',
    role: 'COO',
    company: 'Series B Fintech',
    initials: 'PS',
  },
  {
    quote:
      "The CISO retainer turned our compliance timeline from 6 months to 8 weeks. CxO On Demand made finding the right executive effortless.",
    name: 'James L.',
    role: 'Founder',
    company: 'Healthtech',
    initials: 'JL',
  },
];

export default function TestimonialsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Title animation
      gsap.fromTo(
        titleRef.current,
        { y: 24, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          scrollTrigger: {
            trigger: titleRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Cards animation
      cardsRef.current.forEach((card, index) => {
        if (card) {
          gsap.fromTo(
            card,
            { x: index === 0 ? '-6vw' : '6vw', opacity: 0 },
            {
              x: 0,
              opacity: 1,
              duration: 0.6,
              delay: index * 0.1,
              scrollTrigger: {
                trigger: card,
                start: 'top 85%',
                toggleActions: 'play none none reverse',
              },
            }
          );
        }
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-flowing bg-navy relative z-[100]"
    >
      <div className="w-full px-6 lg:px-12 max-w-7xl mx-auto">
        {/* Title */}
        <div ref={titleRef} className="mb-16 opacity-0">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-teal mb-4 block">
            Testimonials
          </span>
          <h2 className="font-heading text-h2 sm:text-h1 font-bold text-white max-w-2xl">
            Delivered outcomes, validated by teams
          </h2>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.name}
              ref={(el) => { cardsRef.current[index] = el; }}
              className="surface-panel p-8 opacity-0"
            >
              {/* Quote Icon */}
              <div className="mb-6">
                <Quote className="w-10 h-10 text-teal/30" />
              </div>

              {/* Quote Text */}
              <p className="text-lg text-white leading-relaxed mb-8">
                "{testimonial.quote}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal/20 to-teal/5 flex items-center justify-center">
                  <span className="font-heading text-sm font-bold text-teal">
                    {testimonial.initials}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-white">{testimonial.name}</p>
                  <p className="text-sm text-slate">
                    {testimonial.role}, {testimonial.company}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
